import React, { useState } from 'react';
import {
  X, Calendar, Clock, Copy, Check, Video, Trash2, Edit3, Plus, Ban, AlertCircle, Play
} from 'lucide-react';
import { api } from '../../lib/api';
import type { Meeting } from '@sahaj/types';

interface MeetingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  meeting: Meeting | null;
  currentUserId: string;
  onRefresh: () => void;
  onJoinMeeting: (meeting: Meeting) => void;
  onOpenEdit: (meeting: Meeting) => void;
}

export const MeetingDetailsModal: React.FC<MeetingDetailsModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  meeting,
  currentUserId,
  onRefresh,
  onJoinMeeting,
  onOpenEdit,
}) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [guestEmailInput, setGuestEmailInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !meeting) return null;

  const isHost = meeting.host.id === currentUserId;

  const handleCopyLink = () => {
    const linkToCopy = meeting.joinLink || `${window.location.origin}/meet/${meeting.id}`;
    navigator.clipboard.writeText(linkToCopy);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleAddGuestEmail = async () => {
    const email = guestEmailInput.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await api.meetings.addParticipant(workspaceId, meeting.id, {
        guestEmail: email,
        role: 'GUEST',
      });
      setGuestEmailInput('');
      onRefresh();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to add guest email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!window.confirm('Remove this participant from the meeting?')) return;
    try {
      await api.meetings.removeParticipant(workspaceId, meeting.id, participantId);
      onRefresh();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to remove participant');
    }
  };

  const handleStartMeeting = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const updated = await api.meetings.start(workspaceId, meeting.id);
      onRefresh();
      onClose();
      onJoinMeeting(updated);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to start meeting');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelMeeting = async () => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.meetings.cancel(workspaceId, meeting.id);
      onRefresh();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to cancel meeting');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEndMeeting = async () => {
    if (!window.confirm('End this active meeting for all participants?')) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.meetings.end(workspaceId, meeting.id);
      onRefresh();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to end meeting');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTime = (isoString?: string) => {
    if (!isoString) return 'Ad-hoc';
    return new Date(isoString).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-card">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-primary" />
            <div>
              <h2 className="text-base font-semibold text-foreground leading-tight">{meeting.title}</h2>
              <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-full mt-0.5 ${
                meeting.status === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                meeting.status === 'SCHEDULED' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                meeting.status === 'CANCELLED' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                'bg-muted text-muted-foreground'
              }`}>
                {meeting.status}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="p-3 text-xs bg-destructive/15 text-destructive rounded-lg border border-destructive/20 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Description & Metadata */}
          {meeting.description && (
            <p className="text-xs text-muted-foreground bg-muted/20 p-3 rounded-lg border border-border/50">
              {meeting.description}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="p-3 bg-card border border-border rounded-lg space-y-1">
              <div className="text-muted-foreground font-semibold flex items-center gap-1.5 text-[11px]">
                <Calendar className="w-3.5 h-3.5 text-primary" /> Schedule
              </div>
              <div className="font-medium text-foreground">
                {formatDateTime(meeting.startTime)}
              </div>
              <div className="text-[11px] text-muted-foreground">
                Timezone: {meeting.timezone}
              </div>
            </div>

            <div className="p-3 bg-card border border-border rounded-lg space-y-1">
              <div className="text-muted-foreground font-semibold flex items-center gap-1.5 text-[11px]">
                <Clock className="w-3.5 h-3.5 text-primary" /> Type & Host
              </div>
              <div className="font-medium text-foreground">
                {meeting.meetingType} MEETING
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                Host: {meeting.host.displayName}
              </div>
            </div>
          </div>

          {/* Secure Join Link */}
          {meeting.joinLink && (
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-muted-foreground">
                Secure Meeting Link
              </label>
              <div className="flex items-center gap-2 bg-muted/40 p-2 border border-border rounded-lg">
                <input
                  type="text"
                  readOnly
                  value={meeting.joinLink}
                  className="flex-1 text-xs bg-transparent border-none text-foreground focus:outline-none truncate"
                />
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="px-3 py-1.5 text-xs bg-card border border-border hover:bg-muted text-foreground rounded-md font-medium transition flex items-center gap-1 shrink-0"
                >
                  {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedLink ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* Participant Management */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground">
                Participants ({meeting.participants.length})
              </span>
            </div>

            <div className="space-y-1 max-h-44 overflow-y-auto border border-border rounded-lg p-2 bg-muted/10">
              {meeting.participants.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-card border border-border text-xs"
                >
                  <div className="flex items-center gap-2 truncate">
                    <span className="font-medium text-foreground truncate">
                      {p.user ? p.user.displayName : p.guestDisplayName || p.guestEmail}
                    </span>
                    {p.guestEmail && (
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        Guest
                      </span>
                    )}
                    <span className="text-[10px] font-bold text-primary uppercase">
                      {p.role}
                    </span>
                  </div>

                  {isHost && p.role !== 'HOST' && (
                    <button
                      type="button"
                      onClick={() => handleRemoveParticipant(p.id)}
                      className="p-1 text-muted-foreground hover:text-destructive transition"
                      title="Remove participant"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Add External Email Guest */}
          {isHost && meeting.status !== 'CANCELLED' && meeting.status !== 'ENDED' && (
            <div className="space-y-1.5 pt-2 border-t border-border">
              <label className="block text-xs font-semibold text-muted-foreground">
                Invite External Email
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="colleague@external.com"
                  value={guestEmailInput}
                  onChange={(e) => setGuestEmailInput(e.target.value)}
                  className="flex-1 px-3 py-1.5 text-xs bg-muted/40 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button
                  type="button"
                  onClick={handleAddGuestEmail}
                  disabled={isSubmitting}
                  className="px-3 py-1.5 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-lg font-medium transition flex items-center gap-1 disabled:opacity-50"
                >
                  <Plus className="w-3.5 h-3.5" /> Invite
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-border bg-card flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {isHost && meeting.status === 'SCHEDULED' && (
              <>
                <button
                  type="button"
                  onClick={() => onOpenEdit(meeting)}
                  className="px-3 py-1.5 text-xs font-medium bg-card border border-border hover:bg-muted text-foreground rounded-lg transition flex items-center gap-1"
                >
                  <Edit3 className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleCancelMeeting}
                  className="px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 rounded-lg transition flex items-center gap-1 disabled:opacity-50"
                >
                  <Ban className="w-3.5 h-3.5" /> Cancel
                </button>
              </>
            )}

            {isHost && meeting.status === 'ACTIVE' && (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleEndMeeting}
                className="px-3 py-1.5 text-xs font-medium text-destructive bg-destructive/10 hover:bg-destructive/20 rounded-lg transition flex items-center gap-1 disabled:opacity-50"
              >
                End Meeting
              </button>
            )}
          </div>

          <div>
            {meeting.status === 'ACTIVE' ? (
              <button
                type="button"
                onClick={() => {
                  onJoinMeeting(meeting);
                  onClose();
                }}
                className="px-5 py-2 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition shadow-md flex items-center gap-1.5"
              >
                <Play className="w-3.5 h-3.5 fill-current" /> Join Meeting Room
              </button>
            ) : meeting.status === 'SCHEDULED' ? (
              isHost ? (
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleStartMeeting}
                  className="px-5 py-2 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition shadow-md flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Play className="w-3.5 h-3.5 fill-current" /> {isSubmitting ? 'Starting...' : 'Start Meeting'}
                </button>
              ) : (
                <span className="text-xs text-muted-foreground italic font-medium bg-muted/40 px-3 py-1.5 rounded-lg border border-border">
                  <Clock className="w-3.5 h-3.5 inline mr-1" /> Waiting for host to start
                </span>
              )
            ) : (
              <span className="text-xs text-muted-foreground italic font-medium">
                {meeting.status === 'ENDED' ? 'Meeting ended' : 'Meeting cancelled'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
